import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { toast } from '@/components/ui/toaster.jsx';
import {
  getCarrierReadiness,
  runGlobalQuote,
  landGlobalToMarket,
  recordGlobalOutcome,
} from '@/services/submission.service.js';
import ReadinessDialog from './ReadinessDialog.jsx';
import GlobalLadderDialog from './GlobalLadderDialog.jsx';

// "Quote with Global" — the live-API door in the cockpit's Markets surface
// (GLOBAL-COCKPIT-QUOTE-WIRING-SPEC).
//
// The Cockpit V-B4 rework replaced the Submission Builder with a manual-first
// Markets surface, which left the Global integration built, accurate, and
// unreachable: every quote action on the deal was manual. This re-surfaces the
// EXISTING wiring rather than standing up a second Global path — the same
// readiness gate (ReadinessDialog), the same fire (runGlobalQuote), the same
// option-ladder picker (GlobalLadderDialog). Only the landing differs, because
// the Markets surface has no scenario grid to land into: landGlobalToMarket
// re-prices server-side and files the result as a market row.
//
// Manual "Log a submission" / "Enter a quote" stay exactly as they are, for
// portal-logged and non-API carriers.
//
// Four outcomes, all of which have to read honestly:
//   blocked   — the server refused to send (missing airport/runway). Data gap.
//   no terms  — referred or declined. A multi-aircraft deal ALWAYS refers, and
//               that is expected, not a failure. Never renders a priced cell.
//   ladder    — the broker picks the basis. Global's default is the F rung, the
//               dearest of three, so nothing auto-lands.
//   priced    — a quote with no ladder to choose from; lands directly.
// Where each blocker actually gets fixed. The deal root is not a fix surface:
// a missing runway lives on the aircraft's base airport, not the deal landing.
// These are ?door= keys, the app's existing deep-link convention, consumed by
// DealWorkspace (openers edit_insured / edit_roster already exist; 'fleet' is
// handled there as a focus-clearing scroll).
const FIX_DOOR = {
  missing_policyholder_type: 'edit_insured',
  missing_city_state: 'edit_insured',
  // A pilot's answer: the blocker's remedy names the form (?door=pilot_answer&phf=), opened with the answer as the edit.
  blank_certificate: 'pilot_answer',
  certificate_unrecognised: 'pilot_answer',
  // The aircraft's coverage dialog, where the base airport and its runway are
  // set (?door=runway&tail=, DealWorkspace).
  missing_airport: 'runway',
  missing_runway_length: 'runway',
  missing_runway_surface: 'runway',
  no_coverage: 'fleet',
  hull_value_no_basis: 'fleet',
  make_model_unresolved: 'fleet',
  // The shared pre-submit check (utils/carrier-presubmit): every one is the pilot's form.
  pilot_experience_unmatched: 'edit_roster',
  make_model_over_total: 'edit_roster',
  make_model_l12_over_l12: 'edit_roster',
  pilot_forms_disagree: 'edit_roster',
  medical_waiver_unexplained: 'edit_roster',
  // The liability floor (David 2026-11-03): the limits are set on the deal's roster sheet.
  liability_limit_implausible: 'edit_roster',
  liability_limit_unread: 'edit_roster',
};

export default function GlobalQuoteAction({ dealId, carrierName = 'Global Aerospace', onLanded }) {
  const [, setSearchParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [gate, setGate] = useState(null);      // { readiness } while the broker reads it
  // Pre-send refusal. Persistent, because a data gap is something to go fix.
  const [notSent, setNotSent] = useState(null);
  const [alternates, setAlternates] = useState({});
  const [ladder, setLadder] = useState(null);  // { quote, quoteId, ladder, selection, warnings }

  // Our request-side notices plus Global's response-side ones. These are the
  // point of the mapper's warnings contract (coverage date clamped, pilot
  // experience unmatched, entity insured unconfirmed) and must never be
  // swallowed.
  // A list of caveats is reference material, not a notification, so the list
  // itself now rides the market row (blurb tail, rendered as an expandable).
  // This is the pointer to it, one line, no description dump.
  const showWarnings = useCallback((warnings) => {
    if (!warnings?.length) return;
    toast.info(`Global: ${warnings.length} thing${warnings.length === 1 ? '' : 's'} to check, on the market row.`);
  }, []);

  const fire = useCallback(async () => {
    setBusy(true);
    setNotSent(null);
    try {
      const res = await runGlobalQuote(dealId);
      const q = res?.quote;
      const warnings = [...(res?.mapper_warnings || []), ...(q?.warnings || [])].filter(Boolean);

      // Refused before submit: an aircraft with no base airport or runway length
      // null-refs Global's rater into a 500, so the server does not send it. The
      // fix is upstream of Global, on the deal.
      if (res?.blocked) {
        setNotSent({ blockers: res.blockers || [], warnings });
        return;
      }
      if (!q) {
        toast.error('Global returned no quote payload');
        return;
      }

      // No terms: referred, declined, or rated at nothing. Surface the state and
      // the reasons; create NO priced row. Multi-aircraft always lands here —
      // Global refers every fleet to an underwriter.
      // RESOLVES ONTO THE ROW, not a stack of toasts. A referral or a decline
      // is a quote outcome the broker acts on later, so it has to persist and
      // sort with the other markets, carrying its reason and its caveats.
      // Multi-aircraft always lands here: Global refers every fleet.
      if (!q.hasTerms) {
        const reasonList = (q.referDeclineReasons || []).filter(Boolean);
        const outcome = (q.isDeclined || q.producerNotLicensed) ? 'declined' : 'referred';
        const reasons = q.producerNotLicensed
          ? ["Not licensed in this risk's state, Global blocks quoting and binding here.", ...reasonList]
          : reasonList;
        try {
          await recordGlobalOutcome(dealId, { outcome, reasons, warnings, quoteId: res.quote_id });
          toast.info(outcome === 'declined'
            ? 'Global declined. The reason is on the market row.'
            : 'Global referred it to an underwriter. Details are on the market row.');
          if (typeof onLanded === 'function') await onLanded();
        } catch (e) {
          // Nothing would persist, so one short toast is the honest fallback.
          // Still never the multi-clause description dump.
          toast.error(`Global ${outcome}. Could not record it on the row: ${e?.body?.error || e?.message || 'unknown error'}`);
        }
        return;
      }

      // Ladder: hand the pick to the broker and land NOTHING yet. Global's
      // premiums.total is its default F basis; landing it over-quotes (measured
      // $3,100 on a PA-24-250 where the same quote at G is $2,020).
      if (res?.ladder?.available && res.quote_id) {
        setLadder({
          quote: q,
          quoteId: res.quote_id,
          ladder: res.ladder,
          selection: res.ladder_default_selection || { hullByUnitId: {}, liabilityId: null, medicalIndex: null },
          warnings,
        });
        return;
      }

      // An ambiguous multi-aircraft ladder must not fall back to the default
      // premium — that is the over-quote wearing a different hat.
      if (res?.ladder?.ambiguity) {
        toast.error(`Global ${q.quoteNumber || ''}: ${res.ladder.ambiguity.message}`, { duration: 20000 });
        showWarnings(warnings);
        return;
      }

      toast.warning(`Global quote ${q.quoteNumber || ''} carries no option ladder to choose from. Open the quoting workbench to log it manually.`, { duration: 15000 });
      showWarnings(warnings);
    } catch (err) {
      const m = err?.body?.error || err?.message || 'Global quote failed';
      if (/no session|manifest/i.test(m)) {
        toast.error('This deal has no session yet, so Global cannot build the submission. Pre-Stage / Initiate it first.');
      } else {
        toast.error(`Global quote failed: ${m}`);
      }
    } finally {
      setBusy(false);
    }
  }, [dealId, showWarnings, onLanded]);

  // Readiness first. Red blockers stop the fire; amber-only quotes on one
  // acknowledgement. Fail-open by design: if readiness itself errors we still
  // dispatch, because a broken preflight must not become a broken quote button.
  const start = useCallback(async () => {
    setBusy(true);
    let readiness = null;
    try {
      const res = await getCarrierReadiness(dealId, 'global_aerospace');
      readiness = res?.readiness || null;
    } catch (e) {
      // Fail-open stays, but the skip is no longer silent (David 2026-08-12):
      // a submission reaching a market with its preflight skipped is a fact
      // the broker hears in the interface, not the console.
      toast.info(`Preflight check could not run (${e?.message || 'error'}). Dispatching to Global without it.`);
    } finally {
      setBusy(false);
    }
    if (!readiness || readiness.preflight_supported === false) return fire();
    if (readiness.ready && !(readiness.warnings || []).length) return fire();
    setGate({ readiness });
  }, [dealId, fire]);

  const gateRecheck = useCallback(async () => {
    setBusy(true);
    try {
      const res = await getCarrierReadiness(dealId, 'global_aerospace');
      const readiness = res?.readiness || null;
      if (readiness?.ready && !(readiness.warnings || []).length) {
        setGate(null);
        return fire();
      }
      setGate((g) => (g ? { ...g, readiness } : g));
      if (readiness && !readiness.ready) {
        toast.warning(`Still ${readiness.blockers.length} to fix before ${carrierName} can quote`);
      }
    } catch (e) {
      toast.error(`Re-check failed: ${e?.message || 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  }, [dealId, fire, carrierName]);

  const gateProceed = useCallback(() => { setGate(null); return fire(); }, [fire]);

  // Close the gate and land on the surface that fixes THIS blocker. Setting the
  // door on the current route is what makes it work from inside the deal: the
  // path does not change, so a <Link> was a no-op, but DealWorkspace watches
  // the search params and opens the sheet. Unknown codes still close the dialog
  // and say where to go rather than pretending to navigate.
  const handleFix = useCallback((item) => {
    // A row the matcher could not place (pilot_experience_unmatched with a
    // selection) opens the experience selection, the selection riding as state.
    const door = item?.selection ? 'experience_model' : FIX_DOOR[item?.code];
    setGate(null);
    if (!door) {
      toast.info(`Fix this on the deal, then re-check: ${item?.message || 'see the blocker'}`);
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      // The fleet panel lives on the deal card view, so the markets focus has
      // to come off or the broker lands on a pane that does not contain it.
      if (door === 'fleet') next.delete('focus');
      if (door === 'runway' && item?.ref?.tail) next.set('tail', item.ref.tail);
      if (door === 'experience_model') next.set('phf', item.selection.phf_id);
      if (door === 'pilot_answer') {
        const phf = item?.fact_ref?.phf_id || (item?.remedy_link ? new URLSearchParams(item.remedy_link.split('?')[1] || '').get('phf') : null);
        if (!phf) { toast.info(`No pilot form on file to edit: ${item?.message || 'see the blocker'}`); return prev; }
        next.set('phf', phf);
      }
      next.set('door', door);
      return next;
    }, item?.selection ? { state: { experienceSelection: item.selection } } : undefined);
  }, [setSearchParams]);

  // Land the pick. The premium is re-priced SERVER-side from the persisted
  // quote, so what lands is always Global's own number for the chosen basis.
  // The dialog's running total is display only.
  const confirmLadder = useCallback(async () => {
    if (!ladder) return;
    setBusy(true);
    try {
      const res = await landGlobalToMarket(dealId, ladder.quoteId, ladder.selection, Object.values(alternates));
      const dollars = `$${Number(res.premium).toLocaleString('en-US')}`;
      const extra = Array.isArray(res.alternates_landed) && res.alternates_landed.length
        ? ` · +${res.alternates_landed.length} option${res.alternates_landed.length === 1 ? '' : 's'}` : '';
      const basis = (res.option_label ? ` · ${res.option_label}` : '') + extra;
      const q = ladder.quote;
      setLadder(null);
      setAlternates({});
      if (q?.isReferred) {
        toast.warning(`Global quote ${q.quoteNumber || ''} logged at ${dollars}${basis}, referred. Mark it referred before presenting.`, { duration: 15000 });
      } else {
        toast.success(`Global quote ${q?.quoteNumber || ''} logged at ${dollars}${basis}`);
      }
      showWarnings(ladder.warnings);
      if (typeof onLanded === 'function') onLanded();
    } catch (err) {
      const body = err?.body || {};
      toast.error(`Global: ${body.message || body.error || err?.message || 'could not log that selection'}`, { duration: 15000 });
    } finally {
      setBusy(false);
    }
  }, [ladder, dealId, onLanded, showWarnings, alternates]);

  return (
    <>
      {/* Pre-send refusal. NOT a market row: nothing was sent, and 'blocked' on
          the ladder means another broker holds the account. It is a data gap on
          our side, so it sits here until it is fixed rather than flashing past
          in a toast. */}
      {notSent ? (
        <div className="mb-2 w-full rounded-[5px] border border-warning/40 bg-warning-soft px-3 py-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[12px] font-medium text-text">Global was not sent</p>
              <ul className="mt-1 space-y-0.5">
                {notSent.blockers.map((b, i) => (
                  <li key={i} className="text-[11.5px] leading-snug text-text-muted">{b}</li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => setNotSent(null)}
              className="shrink-0 text-[11px] text-text-dim hover:text-text"
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        onClick={start}
        disabled={busy}
        title="Fire a live quote through the Global Aerospace API and pick the hull basis"
        className="inline-flex items-center gap-1.5 rounded-[5px] border border-accent bg-accent px-2.5 py-1 text-[12px] text-white hover:brightness-110 disabled:opacity-50"
      >
        <Zap size={13} strokeWidth={1.7} />
        {busy ? 'Quoting…' : 'Quote with Global'}
      </button>

      <ReadinessDialog
        open={!!gate}
        onOpenChange={(o) => { if (!o) setGate(null); }}
        carrierName={carrierName}
        readiness={gate?.readiness}
        busy={busy}
        onRecheck={gateRecheck}
        onProceed={gateProceed}
        onFix={handleFix}
      />

      <GlobalLadderDialog
        open={!!ladder}
        onOpenChange={(o) => { if (!o) setLadder(null); }}
        ladder={ladder?.ladder}
        selection={ladder?.selection}
        onSelectionChange={(sel) => setLadder((l) => (l ? { ...l, selection: sel } : l))}
        busy={busy}
        onConfirm={confirmLadder}
        alternates={alternates}
        onAlternatesChange={setAlternates}
      />
    </>
  );
}
